import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { checkSequenceAgainstOibInstrument, type OibReferenceWarning } from '../oib/contractCheck';
import { loadOibInstrumentSummary } from '../oib/loader';
import type { OibInstrumentSummary } from '../oib/types';
import { exportSequenceJson, importSequenceJson } from '../state/importExport';
import { findActivePattern } from '../state/projectTypes';
import type { ValidationError } from '../format/validator';
import { BrowserAudioEnvironment } from '../player/webaudio/audioEnvironment';
import { WebAudioPreviewPlayer } from '../player/webaudio/engine';
import type { RuntimeAction } from '../scheduler/actions';
import { RealClock } from '../scheduler/clock';
import { Scheduler } from '../scheduler/scheduler';
import { EventInspector } from './StepEditor/EventInspector';
import { StepGrid } from './StepEditor/Grid';
import { PatternList } from './PatternBank/PatternList';
import { TransportBar } from './Transport/TransportBar';
import { Monitor, type MonitorEntry } from './EventMonitor/Monitor';
import { OibLoader } from './OibPanel/OibLoader';
import { ImportExportPanel } from './ImportExport/ImportExportPanel';
import { useStore } from './StoreContext';
import type { TrackType } from '../format/types';
import { createId } from '../state/defaults';

type TabKey = 'monitor' | 'oib' | 'importExport';

interface AudioBundle {
  readonly env: BrowserAudioEnvironment;
  readonly player: WebAudioPreviewPlayer;
  readonly scheduler: Scheduler;
}

const MAX_MONITOR_ENTRIES = 2000;

export function App(): JSX.Element {
  const { state, dispatch } = useStore();
  const activeDoc = findActivePattern(state);

  const [selectedEventId, setSelectedEventId] = useState<string | undefined>(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [masterVolume, setMasterVolume] = useState(0.8);
  const [monitorEntries, setMonitorEntries] = useState<readonly MonitorEntry[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>('monitor');
  const [oibInstrument, setOibInstrument] = useState<OibInstrumentSummary | undefined>(undefined);
  const [oibLoadErrors, setOibLoadErrors] = useState<readonly string[]>([]);

  const audioRef = useRef<AudioBundle | undefined>(undefined);
  const seqRef = useRef(0);
  const masterVolumeRef = useRef(masterVolume);
  masterVolumeRef.current = masterVolume;
  const activeDocRef = useRef(activeDoc);
  activeDocRef.current = activeDoc;

  const handleAction = useCallback((action: RuntimeAction) => {
    seqRef.current += 1;
    const entry: MonitorEntry = { seq: seqRef.current, action };
    setMonitorEntries((prev) => {
      const next = prev.length >= MAX_MONITOR_ENTRIES ? prev.slice(prev.length - MAX_MONITOR_ENTRIES + 1) : prev;
      return [...next, entry];
    });
    audioRef.current?.player.handleAction(action);
  }, []);

  const ensureAudio = useCallback((): AudioBundle => {
    if (!audioRef.current) {
      const env = new BrowserAudioEnvironment();
      const player = new WebAudioPreviewPlayer({ audio: env });
      player.setMasterVolume(masterVolumeRef.current);
      const scheduler = new Scheduler({
        clock: new RealClock(() => env.currentTime),
        onAction: handleAction,
      });
      audioRef.current = { env, player, scheduler };
      const doc = activeDocRef.current;
      if (doc) {
        scheduler.loadPattern(doc);
        for (const track of doc.tracks) {
          player.setTrackLevel(track.id, track.level);
        }
      }
    }
    return audioRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleAction]);

  const structuralKey = useMemo(() => {
    if (!activeDoc) return '';
    const timing = activeDoc.timing;
    const timingRest = {
      timeSignature: timing.timeSignature,
      stepsPerBeat: timing.stepsPerBeat,
      lengthSteps: timing.lengthSteps,
      loop: timing.loop,
      swing: timing.swing,
    };
    const trackShapes = activeDoc.tracks.map((track) => ({ id: track.id, type: track.type }));
    return JSON.stringify({ id: activeDoc.id, tracks: trackShapes, events: activeDoc.events, timing: timingRest });
  }, [activeDoc]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && activeDoc) {
      audio.scheduler.loadPattern(activeDoc);
      setIsPlaying(false);
      setCurrentStep(0);
      for (const track of activeDoc.tracks) {
        audio.player.setTrackLevel(track.id, track.level);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuralKey]);

  useEffect(() => {
    let raf = 0;
    function frame(): void {
      const audio = audioRef.current;
      if (audio) {
        setIsPlaying((prev) => (prev !== audio.scheduler.isPlaying ? audio.scheduler.isPlaying : prev));
        setCurrentStep((prev) => {
          const next = audio.scheduler.getCurrentStep();
          return prev !== next ? next : prev;
        });
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  const oibWarnings: readonly OibReferenceWarning[] = useMemo(() => {
    if (!activeDoc || !oibInstrument) return [];
    return checkSequenceAgainstOibInstrument(activeDoc, oibInstrument);
  }, [activeDoc, oibInstrument]);

  const selectedEvent = activeDoc?.events.find((event) => event.id === selectedEventId);

  useEffect(() => {
    if (selectedEventId !== undefined && selectedEvent === undefined) {
      setSelectedEventId(undefined);
    }
  }, [selectedEvent, selectedEventId]);

  function handlePlay(): void {
    const audio = ensureAudio();
    void audio.env.resume();
    audio.scheduler.play();
    setIsPlaying(audio.scheduler.isPlaying);
  }

  function handleStop(): void {
    audioRef.current?.scheduler.stop();
    setIsPlaying(false);
    setCurrentStep(0);
  }

  function handleRestart(): void {
    const audio = ensureAudio();
    void audio.env.resume();
    audio.scheduler.restart();
    setIsPlaying(audio.scheduler.isPlaying);
  }

  function handlePanic(): void {
    audioRef.current?.scheduler.panic();
    audioRef.current?.player.panic();
    setIsPlaying(false);
    setCurrentStep(0);
  }

  function handleBpmChange(bpm: number): void {
    const clamped = Math.min(300, Math.max(30, Math.round(bpm)));
    dispatch({ type: 'SET_BPM', bpm: clamped });
    audioRef.current?.scheduler.setBpm(clamped);
  }

  function handleLoopChange(loop: boolean): void {
    dispatch({ type: 'SET_LOOP', loop });
  }

  function handleMasterVolumeChange(value: number): void {
    setMasterVolume(value);
    audioRef.current?.player.setMasterVolume(value);
  }

  function handleMute(trackId: string, mute: boolean): void {
    dispatch({ type: 'SET_TRACK_MUTE', trackId, mute });
    audioRef.current?.scheduler.setMute(trackId, mute);
  }

  function handleSolo(trackId: string, solo: boolean): void {
    dispatch({ type: 'SET_TRACK_SOLO', trackId, solo });
    audioRef.current?.scheduler.setSolo(trackId, solo);
  }

  function handleTrackLevel(trackId: string, level: number): void {
    dispatch({ type: 'SET_TRACK_LEVEL', trackId, level });
    audioRef.current?.player.setTrackLevel(trackId, level);
  }

  function handleClearPattern(): void {
    if (window.confirm('Pattern wirklich leeren? Alle Steps werden entfernt.')) {
      dispatch({ type: 'CLEAR_PATTERN' });
      setSelectedEventId(undefined);
    }
  }

  function handleLoadOibFile(text: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setOibLoadErrors(['Datei ist kein gueltiges JSON.']);
      return;
    }
    const result = loadOibInstrumentSummary(parsed);
    if (!result.ok || !result.value) {
      setOibLoadErrors(result.errors.length > 0 ? result.errors : ['Datei konnte nicht als Instrument gelesen werden.']);
      return;
    }
    setOibInstrument(result.value);
    setOibLoadErrors(result.errors);
  }

  function handleImport(text: string): readonly ValidationError[] | undefined {
    const result = importSequenceJson(state, text);
    if (!result.ok || !result.project) {
      return result.errors ?? [];
    }
    dispatch({ type: 'LOAD_PROJECT', project: result.project });
    return [];
  }

  function handleExport(): string {
    if (!activeDoc) return '{}';
    return exportSequenceJson(activeDoc);
  }

  if (!activeDoc) {
    return <p>Kein aktives Pattern vorhanden.</p>;
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <h1>Open Sequence &amp; Arrangement Lab</h1>
        <p className="tagline">Portabler Editor und Player fuer zeitbasierte musikalische und steuernde Ereignisse.</p>
      </header>

      <TransportBar
        isPlaying={isPlaying}
        currentStep={currentStep}
        bpm={activeDoc.timing.bpm}
        loop={activeDoc.timing.loop}
        masterVolume={masterVolume}
        onPlay={handlePlay}
        onStop={handleStop}
        onRestart={handleRestart}
        onPanic={handlePanic}
        onBpmChange={handleBpmChange}
        onLoopChange={handleLoopChange}
        onMasterVolumeChange={handleMasterVolumeChange}
      />

      <PatternList
        patterns={state.patterns}
        activePatternId={state.activePatternId}
        onSelect={(patternId) => dispatch({ type: 'SELECT_PATTERN', patternId })}
        onNew={() => dispatch({ type: 'NEW_PATTERN' })}
        onDuplicate={(patternId) => dispatch({ type: 'DUPLICATE_PATTERN', patternId })}
        onRename={(patternId, name) => dispatch({ type: 'RENAME_PATTERN', patternId, name })}
        onDelete={(patternId) => dispatch({ type: 'DELETE_PATTERN', patternId })}
      />

      <div className="pattern-settings">
        <label className="field">
          Laenge (Steps)
          <input
            type="number"
            min={1}
            max={64}
            value={activeDoc.timing.lengthSteps}
            onChange={(event) => dispatch({ type: 'SET_LENGTH_STEPS', lengthSteps: Number(event.target.value) })}
          />
        </label>
        <button type="button" onClick={handleClearPattern}>
          Pattern leeren
        </button>
      </div>

      <div className="workspace">
        <StepGrid
          doc={activeDoc}
          currentStep={currentStep}
          isPlaying={isPlaying}
          selectedEventId={selectedEventId}
          onCreateStep={(trackId, step) => {
            const eventId = createId('evt');
            dispatch({ type: 'TOGGLE_STEP', trackId, step, eventId });
            setSelectedEventId(eventId);
          }}
          onSelectEvent={setSelectedEventId}
          onRenameTrack={(trackId, name) => dispatch({ type: 'RENAME_TRACK', trackId, name })}
          onSetTrackType={(trackId, trackType: TrackType) => dispatch({ type: 'SET_TRACK_TYPE', trackId, trackType })}
          onSetTrackRole={(trackId, role) => dispatch({ type: 'SET_TRACK_ROLE', trackId, role })}
          onSetTrackMute={handleMute}
          onSetTrackSolo={handleSolo}
          onSetTrackLevel={handleTrackLevel}
          onRemoveTrack={(trackId) => dispatch({ type: 'REMOVE_TRACK', trackId })}
          onClearTrack={(trackId) => dispatch({ type: 'CLEAR_TRACK', trackId })}
          onAddTrack={() => dispatch({ type: 'ADD_TRACK' })}
        />
        <EventInspector
          event={selectedEvent}
          onPatch={(eventId, patch) => dispatch({ type: 'UPDATE_EVENT', eventId, patch })}
          onDelete={(eventId) => {
            dispatch({ type: 'REMOVE_EVENT', eventId });
            setSelectedEventId(undefined);
          }}
        />
      </div>

      <nav className="tabs" role="tablist">
        <button type="button" role="tab" aria-selected={activeTab === 'monitor'} onClick={() => setActiveTab('monitor')}>
          Ereignisse
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'oib'} onClick={() => setActiveTab('oib')}>
          OIB-Integration
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'importExport'}
          onClick={() => setActiveTab('importExport')}
        >
          Import / Export
        </button>
      </nav>

      <section className="tab-content">
        {activeTab === 'monitor' && <Monitor entries={monitorEntries} onClear={() => setMonitorEntries([])} />}
        {activeTab === 'oib' && (
          <OibLoader
            instrument={oibInstrument}
            loadErrors={oibLoadErrors}
            warnings={oibWarnings}
            onLoadFile={handleLoadOibFile}
            onClear={() => {
              setOibInstrument(undefined);
              setOibLoadErrors([]);
            }}
          />
        )}
        {activeTab === 'importExport' && (
          <ImportExportPanel onImport={handleImport} onExport={handleExport} activePatternName={activeDoc.name} />
        )}
      </section>
    </div>
  );
}
